import {Observable, ReplaySubject, Subject, Subscription, tap, timer, withLatestFrom} from "rxjs";
import {Settings} from "./settings";
import {Onset} from "./core";
import {BarEvent} from "./bar-events";
import {BarTreeGenerator, createBarEventStream} from "./bar-tree-generator";

/**
 * Owns bar-event generation and look-ahead scheduling of {@link Onset}s.
 *
 * Pulls {@link BarEvent}s from the demand-driven bar event stream into a
 * buffer, and every `lookAhead` milliseconds converts enough of them into
 * timestamped onsets to fill the `scheduleAheadTime` window, publishing them
 * on {@link onsets$}. Timestamps are derived from the injected audio clock,
 * so the caller decides which clock the schedule runs against.
 *
 * Durations are emitted in beats by the stream and converted to seconds here,
 * so tempo changes apply live, within the look-ahead window. Structural
 * settings take effect when the next bar is generated.
 *
 * This is deliberately pull-based (the classic Web Audio "two clocks"
 * pattern): audio scheduling must run ahead of wall-clock time, which a
 * push-based stream cannot do on its own.
 */
class ClickScheduler {

    private readonly settings: Settings;
    private readonly currentTime: () => number;

    // how often to call schedule(), milliseconds
    private readonly lookAhead: number = 25;
    // how far ahead to schedule audio, seconds
    private readonly scheduleAheadTime: number = 0.1;
    private schedulerSubscription: Subscription | undefined;

    private readonly barRequests$ = new Subject<void>();
    private barBuffer: BarEvent[] = [];

    // replays the most recent onset so the scheduler can compute the next
    // timestamp from it
    private readonly onsetStream$ = new ReplaySubject<Onset>(1);
    readonly onsets$: Observable<Onset> = this.onsetStream$;

    private scheduler$ = timer(0, this.lookAhead)
        .pipe(
            withLatestFrom(this.onsetStream$), // get the most recent (previous) onset event
            tap(([, onset]) => this.schedule(onset)),
        );

    /**
     * Duration of one beat in seconds. Read live from settings so tempo changes
     * apply to the next scheduled onset, within the look-ahead window.
     */
    private get beatDuration(): number {
        return 60 / this.settings.bpm;
    }

    constructor(settings: Settings, currentTime: () => number) {
        this.settings = settings;
        this.currentTime = currentTime;

        const generator = new BarTreeGenerator(this.settings);
        createBarEventStream(this.barRequests$, generator, this.settings)
            .subscribe(event => this.barBuffer.push(event));
    }

    /**
     * Pre-schedule the count-in (beatsPerBar evenly spaced, always sounding,
     * with the first as the downbeat) and start the look-ahead loop.
     */
    start() {
        const firstTimestamp = this.currentTime() + this.scheduleAheadTime;

        for (let i = 0; i < this.settings.beatsPerBar; i++) {
            this.onsetStream$.next({
                duration: this.beatDuration,
                timestamp: firstTimestamp + (i * this.beatDuration),
                isRest: false,
                isDownBeat: i === 0,
            });
        }

        this.schedulerSubscription = this.scheduler$.subscribe();
    }

    /**
     * Stop scheduling and clear the buffered bar, so the next start begins
     * cleanly at a downbeat.
     */
    stop() {
        this.schedulerSubscription?.unsubscribe();
        this.barBuffer = [];
    }

    private schedule(previousOnset: Onset) {
        const onsets: Onset[] = [];

        let nextTimestamp = previousOnset.timestamp + previousOnset.duration;

        while (nextTimestamp < (this.currentTime() + this.scheduleAheadTime)) {
            const nextEvent = this.nextEvent();
            const duration = nextEvent.duration * this.beatDuration;

            onsets.push({
                duration: duration,
                timestamp: nextTimestamp,
                isRest: nextEvent.isRest,
                isDownBeat: nextEvent.isDownbeat,
            });

            nextTimestamp += duration;
        }

        onsets.forEach(onset => this.onsetStream$.next(onset));
    }

    private nextEvent(): BarEvent {
        if (this.barBuffer.length === 0) {
            // synchronously refills the buffer with a fresh bar
            this.barRequests$.next();
        }
        return this.barBuffer.shift()!;
    }
}

export {ClickScheduler};
